import { CommonModule, Location } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { PatientProvider } from '../../../care-team/models/patient-provider';
import { getPatientCareTeam } from '../../../care-team/store/actions/care-team.actions';
import { selectCareTeam, selectIsLoading } from '../../../care-team/store/selectors/care-team.selector';
import { loadPageWithPatientIdParameter } from '../../../core/store/actions/app.actions';
import { selectIsMobileBrowser } from '../../../core/store/selectors/app.selector';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { PageHeaderComponent } from '../../../shared/components/page-header/page-header.component';
import { ProgressSpinnerComponent } from '../../../shared/components/progress-spinner/progress-spinner.component';

@Component({
  selector: 'app-message-provider-selection',
  imports: [CommonModule, LetDirective, IconComponent, MatButtonModule, ProgressSpinnerComponent, PageHeaderComponent],
  templateUrl: './provider-selection.component.html',
  styleUrl: './provider-selection.component.scss',
})
export class MessageProviderSelectionComponent implements OnInit {
  private store = inject(Store);
  private location = inject(Location);
  @Input() isModal: boolean = false;
  @Output() providerUserIdSelected = new EventEmitter<string>();

  careTeam$ = this.store.select(selectCareTeam);
  isLoading$ = this.store.select(selectIsLoading);
  isMobileBrowser = this.store.selectSignal(selectIsMobileBrowser);
  searchText = signal('');
  selectedProviderUserId = signal<string | null>(null);

  filterCareTeam(list: PatientProvider[] | null | undefined): PatientProvider[] {
    const q = this.searchText().trim().toLowerCase();
    if (!list || list.length === 0) return [];
    if (!q) return list;

    return list.filter(p => {
      const name = (p.displayName ?? '').toLowerCase();
      const team = (this.getPrimaryTeamName(p) ?? '').toLowerCase();
      return name.includes(q) || team.includes(q);
    });
  }

  ngOnInit(): void {
    this.store.dispatch(getPatientCareTeam());
  }

  getPrimaryTeamName(patientProvider: PatientProvider): string {
    return patientProvider.teams.find(team => team.isPrimary)?.name || '';
  }

  select(providerUserId: string) {
    if (this.isMobileBrowser()) {
      this.selectedProviderUserId.set(providerUserId);
    } else {
      this.providerUserIdSelected.emit(providerUserId);
    }
  }

  onRowKeydown(event: KeyboardEvent, providerUserId: string) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.select(providerUserId);
    }
  }

  onClearSearch(inputEl: HTMLInputElement) {
    this.searchText.set('');
    queueMicrotask(() => inputEl.focus());
  }

  onCancel() {
    this.selectedProviderUserId.set(null);
    this.searchText.set('');
    this.location.back();
  }

  onContinue() {
    const providerUserId = this.selectedProviderUserId();
    if (!providerUserId) return;

    if (this.isMobileBrowser()) {
      this.store.dispatch(loadPageWithPatientIdParameter({ route: `messages/${providerUserId}/topic-selection` }));
    } else {
      this.providerUserIdSelected.emit(providerUserId);
    }
  }
}
